import classNames from "classnames";
import { memo } from "react";
import type { IntakeQuestion } from "../../registry/types";
import * as css from "./intake-modal.module.css";

type IntakeOptionLayout = NonNullable<IntakeQuestion["layout"]>;
type IntakeOptionData = IntakeQuestion["options"][number];

export interface IntakeOptionProps {
  option: IntakeOptionData;
  layout: IntakeOptionLayout;
  isSelected: boolean;
  disabled?: boolean;
  onClick: () => void;
}

/**
 * One option in the intake question. Two layouts share the same base
 * surface (`.option`) and selected state (`.optionSelected`); each
 * adds its own shape + chrome:
 *   - "list" — full-width card with icon-box + label + radio dot.
 *   - "grid" — compact pill with icon + label, no radio (selection
 *     communicated by the border/bg alone).
 */
export const IntakeOption = memo(function IntakeOption({
  option,
  layout,
  isSelected,
  disabled,
  onClick,
}: IntakeOptionProps) {
  const { Icon } = option;
  const className = classNames(
    css.option,
    layout === "grid" ? css.optionPill : css.optionCard,
    isSelected && css.optionSelected,
  );
  return (
    <button
      aria-pressed={isSelected}
      className={className}
      disabled={disabled}
      onClick={onClick}
      type="button"
    >
      {layout === "grid" ? (
        <>
          {Icon ? <Icon className={css.optionPillIcon} /> : null}
          <span className={css.optionPillLabel}>{option.label}</span>
        </>
      ) : (
        <>
          <span className={css.optionContent}>
            {Icon ? (
              <span className={css.optionIconBox}>
                <Icon className={css.optionIcon} />
              </span>
            ) : null}
            <span className={css.optionLabel}>{option.label}</span>
          </span>
          <span className={css.optionRadio}>
            <span className={css.optionRadioInner} />
          </span>
        </>
      )}
    </button>
  );
});
